import React from 'react';

import ChecklistItem from './CheckListItem';
import {
  CHECKLIST_ITEM_STATE_ACTIVE, CHECKLIST_ITEM_STATE_COMPLETE,
  CHECKLIST_ITEM_STATE_INCOMPLETE, TradeListLoadingStates,
} from '../reducer/constants';

const CHECKLIST_STATES = {
  PROPERTY: 1,
  METERS: 2,
  RULES: 3,
  TRADES: 4,
  DATA_PREPARED: 5,
  DATA_RENDERED: 6,
};

/**
 * Single source of truth for state metadata.
 * Maps each loading state to its checklist completion level and progress percentage.
 *
 * The `completedUpTo` property represents the highest checklist item order that should
 * be marked as COMPLETE when that loading state is active. This creates an intentional
 * "off by 1" pattern where the value indicates completed prerequisites rather than the
 * currently active stage.
 *
 * For example, when in LOADING_METERS state:
 * - completedUpTo = CHECKLIST_STATES.PROPERTY (1)
 * - This means: Property loading is COMPLETE ✓, Meters are ACTIVE ⟳
 *
 * This design allows the checklist UI to correctly show:
 * - Items with order <= completedUpTo as COMPLETE (✓)
 * - Item with order = completedUpTo + 1 as ACTIVE (⟳)
 * - Items with order > completedUpTo + 1 as INCOMPLETE (○)
 * @type {{[key:string]: {completedUpTo: number, progress: number}}}
 */
const STATE_METADATA = Object.freeze({
  [TradeListLoadingStates.IDLE]: { completedUpTo: 0, progress: 0 },
  [TradeListLoadingStates.INITIALISING]: { completedUpTo: 0, progress: 5 },
  [TradeListLoadingStates.LOADING_METERS]: {
    completedUpTo: CHECKLIST_STATES.PROPERTY,
    progress: 15,
  },
  [TradeListLoadingStates.LOADING_RULES]: {
    completedUpTo: CHECKLIST_STATES.METERS,
    progress: 30,
  },
  [TradeListLoadingStates.LOADING_TRADES]: {
    completedUpTo: CHECKLIST_STATES.RULES,
    progress: 60,
  },
  [TradeListLoadingStates.PREPARING_DATA]: {
    completedUpTo: CHECKLIST_STATES.TRADES,
    progress: 90,
  },
  [TradeListLoadingStates.RENDERING_DATA]: {
    completedUpTo: CHECKLIST_STATES.DATA_PREPARED,
    progress: 95,
  },
  [TradeListLoadingStates.COMPLETE]: {
    completedUpTo: CHECKLIST_STATES.DATA_RENDERED,
    progress: 100,
  },
  [TradeListLoadingStates.ERROR]: { completedUpTo: 0, progress: 0 },
});

/**
 * Checklist items in the order they appear in the checklist.
 * @type {{order: number}[]}
 */
const CHECKLIST_ITEMS = Object.freeze([
  { order: CHECKLIST_STATES.PROPERTY },
  { order: CHECKLIST_STATES.METERS },
  { order: CHECKLIST_STATES.RULES },
  { order: CHECKLIST_STATES.TRADES },
  { order: CHECKLIST_STATES.DATA_PREPARED },
  { order: CHECKLIST_STATES.DATA_RENDERED },
]);

/**
 * Determines the visual state of a checklist item based on current progress.
 * @param {number} completedUpTo - The highest checklist order that has been completed
 * @param {number} itemOrder - The order of the checklist item being evaluated
 * @returns {string} The state of the checklist item (complete, active, or incomplete)
 */
const checklistState = (completedUpTo, itemOrder) => {
  if (completedUpTo >= itemOrder) {
    return CHECKLIST_ITEM_STATE_COMPLETE;
  }

  if (itemOrder === completedUpTo + 1) {
    return CHECKLIST_ITEM_STATE_ACTIVE;
  }

  return CHECKLIST_ITEM_STATE_INCOMPLETE;
};

/**
 * Formats the appropriate label for a loading stage based on its order and completion status.
 * This function determines whether to show a "loading" or "loaded" message for each stage,
 * and includes dynamic count information for meters, rules, and trades when available.
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object for internationalization
 * @param {number} params.order - The stage order (1-6) representing which loading stage
 * to get the label for
 * @param {number} params.completedUpTo - The highest checklist order that has been completed
 * @param {string} params.currentState - The current loading state string
 * @param {object} params.metadata - The metadata object containing counts (metersCount,
 * rulesCount, tradesCount) for each stage
 * @returns {string|React.ReactElement} The label for the specified stage.
 * Returns a string for simple labels or a React element with formatted counts
 * and pluralization for stages 2-4.
 */
const formatStageLabel = ({
  intl,
  order,
  completedUpTo,
  currentState,
  metadata,
}) => {
  const { metersCount = 0, rulesCount = 0, tradesCount = 0 } = metadata || {};
  const isCompleted = completedUpTo >= order;

  switch (order) {
    case CHECKLIST_STATES.PROPERTY: {
      const propertyLoadedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.property_loaded.label',
        defaultMessage: 'Property loaded',
      });
      const loadingPropertyProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_property.label',
        defaultMessage: 'Loading property',
      });
      return isCompleted ? propertyLoadedProgressBarLabel : loadingPropertyProgressBarLabel;
    }

    case CHECKLIST_STATES.METERS: {
      const isLoadingMetersWithCount = currentState === TradeListLoadingStates.LOADING_METERS
        && metersCount > 0;

      if (isCompleted || isLoadingMetersWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.meters_loaded.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> meter} other {<strong>#</strong> meters}} loaded',
          },
          { count: metersCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }

      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_meters.label',
        defaultMessage: 'Loading meters',
      });
    }

    case CHECKLIST_STATES.RULES: {
      // Show dynamic count during loading if we have any rules loaded
      const isLoadingRulesWithCount = currentState === TradeListLoadingStates.LOADING_RULES
        && rulesCount > 0;

      if (isCompleted || isLoadingRulesWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.trade_rules_loading.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade rule} other {<strong>#</strong> trade rules}} loaded',
          },
          { count: rulesCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }
      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_rules.label',
        defaultMessage: 'Loading rules',
      });
    }

    case CHECKLIST_STATES.TRADES: {
      // Show dynamic count during loading if we have any trades loaded
      const isLoadingTradesWithCount = currentState === TradeListLoadingStates.LOADING_TRADES
        && tradesCount > 0;

      if (isCompleted || isLoadingTradesWithCount) {
        return intl.formatMessage(
          {
            id: 'trade.trade_list.helpers.format_stage_label.checklist.overlay.trades_loading.overlay.body.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade} other {<strong>#</strong> trades}} loaded',
          },
          { count: tradesCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }
      return intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.loading_trades.label',
        defaultMessage: 'Loading trades',
      });
    }

    case CHECKLIST_STATES.DATA_PREPARED: {
      const dataPreparedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.data_prepared.label',
        defaultMessage: 'Data prepared',
      });
      const preparingDataProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.preparing_data.label',
        defaultMessage: 'Preparing data',
      });
      return isCompleted ? dataPreparedProgressBarLabel : preparingDataProgressBarLabel;
    }

    case CHECKLIST_STATES.DATA_RENDERED: {
      const dataRenderedProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.data_rendered.label',
        defaultMessage: 'Data rendered',
      });
      const renderingDataProgressBarLabel = intl.formatMessage({
        id: 'trade.trade_list.helpers.format_stage_label.checklist.rendering_data.label',
        defaultMessage: 'Rendering data',
      });
      return isCompleted ? dataRenderedProgressBarLabel : renderingDataProgressBarLabel;
    }

    default:
      return '';
  }
};

/**
 * Returns a checklist-style progress label showing all loading stages with dynamic messages.
 * Supports three visual states: completed (green), active (yellow), and pending (grey).
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object
 * @param {string} params.currentState - The current loading state
 * @param {object} params.metadata - The metadata object containing counts for each stage
 * @returns {React.ReactElement} A JSX element containing the checklist
 */
const displayChecklistProgress = ({ intl, currentState, metadata }) => {
  const completedUpTo = STATE_METADATA[currentState]?.completedUpTo ?? 0;

  return (
    <div>
      {CHECKLIST_ITEMS.map((item) => {
        const state = checklistState(completedUpTo, item.order);
        const label = formatStageLabel({
          intl,
          order: item.order,
          completedUpTo,
          currentState,
          metadata,
        });
        return (
          <ChecklistItem key={item.order} state={state} label={label} />
        );
      })}
    </div>
  );
};

/**
 * Generates loading overlay configuration based on the current loading state.
 * This function maps the TradeList loading state machine's current state to
 * the appropriate overlay configuration, providing user feedback during the
 * multi-stage trade data fetching pipeline. Each loading stage displays
 * a checklist showing all stages with checkboxes marked as completed.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object for internationalization.
 * Used to format all overlay text messages with proper locale support.
 * @param {object} params.date - A Luxon DateTime object representing the date
 * being loaded. Displayed in the overlay body to provide context to the user.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @returns {{ loadingOverlayProps: object|null, isOverlayVisible: boolean }} Overlay configuration
 * object with `loadingOverlayProps` and `isOverlayVisible` properties. See return value
 * structure documentation above.
 */
const buildLoadingOverlayState = ({
  intl,
  date,
  loadingState,
}) => {
  if (!loadingState || !intl || !date) {
    return { loadingOverlayProps: null, isOverlayVisible: false };
  }
  const { state, metadata } = loadingState || {};

  const headerLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.build_loading_overlay_state.overlay.header.label',
    defaultMessage: 'Loading Trade Data',
  });
  const bodyLabel = intl.formatMessage({
    id: 'trade.trade_list.helpers.build_loading_overlay_state.overlay.body.label',
    defaultMessage: 'Loading trade data for {date}:',
  }, { date: date.toISODate() });

  // Build the checklist progress label with metadata
  const progressLabel = displayChecklistProgress({ intl, currentState: state, metadata });

  // Total number of checklist stages (PROPERTY through DATA_RENDERED)
  const totalSteps = Object.keys(CHECKLIST_STATES).length;

  switch (state) {
    case TradeListLoadingStates.IDLE:
    case TradeListLoadingStates.ERROR:
      return { loadingOverlayProps: null, isOverlayVisible: false };
    case TradeListLoadingStates.INITIALISING:
    case TradeListLoadingStates.LOADING_METERS:
    case TradeListLoadingStates.LOADING_RULES:
    case TradeListLoadingStates.LOADING_TRADES:
    case TradeListLoadingStates.PREPARING_DATA:
    case TradeListLoadingStates.RENDERING_DATA:
    case TradeListLoadingStates.COMPLETE:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          progressLabel,
          updateProgress: () => {},
          progressBarValue: STATE_METADATA[state]?.progress ?? 0,
          currentStep: STATE_METADATA[state]?.completedUpTo ?? 0,
          totalSteps,
        },
        isOverlayVisible: true,
      };
    default:
      return { loadingOverlayProps: null, isOverlayVisible: false };
  }
};

export default buildLoadingOverlayState;
